<template>
  <div class="form-card">

    <div class="content-wrap">

      <div
        ref="editorRef"
        class="editor"
        contenteditable="true"
        @click="handleClick"
        @input="handleInput"
        @keydown="handleKeydown"
      ></div>

    </div>

    <!-- 底部工具栏 -->
    <div class="toolbars">
      <div class="toolbars-left">

        <!-- <a-button
          type="text"
          class="btn-upload"
          @click="$emit('upload')"
        >
          <img
            src="@/assets/casesearchImg/icon-up.png"
            class="icons"
          />
          加密上传文件
        </a-button> -->

        <!-- <a-button
          type="text"
          class="btn-ai"
          @click="$emit('optimize')"
        >
          <img
            src="@/assets/casesearchImg/icon-recognition.png"
            class="icons"
          />
          智能优化问题
        </a-button> -->

      </div>

      <div class="toolbars-right">

        <div class="tag-btn">

          <img
            src="@/assets/docImg/doc-tag.png"
            class="tag-icon"
          />

          {{ formTitle }}

        </div>

        <a-button
          class="btn-search"
          size="large"
          @click="handleSubmit"
        >
          <img
            src="@/assets/casesearchImg/i-search.png"
            class="search-icon"
          />

           开始生成

        </a-button>

      </div>
    </div>

  </div>
</template>

<script setup>
defineOptions({
  name: "DocumentForm",
})

import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted
} from "vue"

const props = defineProps({
  modelValue: String,
  templateType: String
})

const emit = defineEmits([
  "update:modelValue",
  "submit",
  "upload",
  "optimize"
])

const editorRef = ref()

const templateMap = {
  complaint: {
    title: "起诉状",
    fields: [
      ["我是", "姓名或名称"],
      ["现起诉被告", "姓名或名称"],
      ["本案的案件背景是：", "请介绍案件背景"],
      ["诉讼请求是：", "请列举诉讼请求"],
    ],
  },

  reply: {
    title: "答辩状",
    fields: [
      ["我是本案被告", "填写我方身份名称"],
      ["原告起诉事实简述：", "简述原告起诉内容"],
      ["我方答辩意见：", "陈述抗辩事实理由"],
      ["争议焦点：", "双方核心分歧点"],
    ],
  },

  appeal: {
    title: "上诉状",
    fields: [
      ["一审案号：", "填写原审案件编号"],
      ["上诉人：", "姓名或名称"],
      ["被上诉人：", "姓名或名称"],
      ["一审不服事实与理由：", "不服原判的事实与法律问题"],
      ["上诉请求：", "本次上诉诉求"],
    ],
  },

  defense: {
    title: "刑事辩护意见",
    fields: [
      ["当事人罪名：", "涉嫌刑事罪名"],
      ["案件事实：", "完整案件经过"],
      ["从轻/减轻辩护要点：", "可从轻、减轻处罚情节"],
      ["证据存在问题：", "证据质证异议点"],
    ],
  },

  evidence: {
    title: "证据清单",
    fields: [
      ["案件类型：", "民事/刑事/行政案件"],
      ["证据清单内容：", "逐条填写证据、来源、证明目的"],
    ],
  }
}

const currentTemplate = computed(() => {
  return templateMap[props.templateType] || templateMap.complaint
})

const formTitle = computed(() => currentTemplate.value.title)

/**
 * 从父组件传入的 modelValue（已拼接的文本）中按前缀提取各字段值
 * 保留用户已填写的内容，避免重复创建编辑器时丢失
 */
const prefillFromModelValue = () => {
  if (!props.modelValue) return {};
  const lines = props.modelValue
    .split("\n")
    .map(l => l.trim())
    .filter(Boolean);
  const knownPrefixes = currentTemplate.value.fields.map(([label]) => label);
  // footer 行（如"请基于上述信息起草一份起诉状。"）作为跨行收集的停止边界，
  // 防止把 footer 内容也算进"诉讼请求"等最后一个字段里
  const footerRegex = /^请基于上述信息起草一份/;
  const filled = {};
  knownPrefixes.forEach((label) => {
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith(label)) {
        let rest = lines[i].slice(label.length);
        const cleaned = rest.replace(/^[：:\s，,、。.？?!！]+/, "").trim();
        const placeholders = currentTemplate.value.fields
          .map(([, ph]) => ph)
          .filter(p => p);
        if (cleaned && !placeholders.includes(cleaned) && cleaned.length > 0) {
          filled[label] = cleaned;
        } else {
          // 跨行内容收集，到下一个标题或 footer 行停止
          const parts = [];
          for (let j = i + 1; j < lines.length; j++) {
            const next = lines[j].trim();
            if (!next) continue;
            if (knownPrefixes.some(p => next.startsWith(p))) break;
            if (footerRegex.test(next)) break;
            parts.push(next);
          }
          if (parts.length) filled[label] = parts.join("\n").trim();
        }
        break;
      }
    }
  });
  return filled;
};

const createEditor = (skipEmit = false) => {
  if (!editorRef.value) return;

  // 清空编辑器
  editorRef.value.innerHTML = "";

  // 关键修复：保留父组件传入的 modelValue 中已填过的字段
  const prefilled = prefillFromModelValue();

  currentTemplate.value.fields.forEach(([labelText, placeholder]) => {
    // 一行
    const line = document.createElement("div");
    line.className = "editor-line";

    // 左侧文字
    const label = document.createElement("span");
    label.className = "editor-label";
    label.contentEditable = "true";
    label.spellcheck = false;
    label.innerText = labelText;

    // 右侧占位 - 如果有已填内容则填充，否则显示占位
    const token = document.createElement("span");
    token.className = "editor-token";
    token.contentEditable = "true";
    token.spellcheck = false;
    token.dataset.placeholder = placeholder;
    token.innerText = prefilled[labelText] || placeholder;

    line.appendChild(label);
    line.appendChild(token);

    editorRef.value.appendChild(line);
  });

  // 最后一行
  const footer = document.createElement("div");
  footer.className = "editor-line";
  footer.contentEditable = "true";
  footer.spellcheck = false;
  footer.innerText = `请基于上述信息起草一份${formTitle.value}。`;

  editorRef.value.appendChild(footer);

  // 首次创建时 emit 一次让父组件拿到初始值；
  // 重建（templateType 变化）时不 emit，避免用占位文本覆盖父组件已保存的 keyword
  if (!skipEmit) {
    emitText();
  }
};

const emitText = () => {
  if (!editorRef.value) return;

  const lines = [];

  editorRef.value.querySelectorAll(".editor-line").forEach(line => {
    lines.push(line.innerText.trim());
  });

  emit("update:modelValue", lines.join("\n"));
};

const handleClick = (e) => {
  const target = e.target;

  if (!target.classList.contains("editor-token")) return;

  if (target.innerText.trim() === target.dataset.placeholder) {
    target.innerText = "";

    // 光标放进去
    const range = document.createRange();
    const selection = window.getSelection();

    range.selectNodeContents(target);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);
  }
};

const handleInput = () => {
  // 不再把空 token 立即填充为占位文本，让 token 真正能变空，
  // 这样"删除输入框"才能真正生效，触发"全空"判断自动切回 DocumentInput
  // （占位文本由 CSS :empty::before 在为空时显示，无需 JS 维护）

  emitText();

  // 编辑器未初始化完成时跳过全空判断（避免初始化阶段被误切）
  if (!isEditorInitialized) return;

  // 判定"所有 token 都为空字符串" → 切回 searched=1
  let allEmpty = true;
  editorRef.value.querySelectorAll('.editor-token').forEach(token => {
    if (token.innerText.trim() !== "") {
      allEmpty = false;
    }
  });
  if (allEmpty) {
    setTimeout(() => emit('back'), 0);
  }
};

function handleKeydown(){

    nextTick(()=>{

        emitText()

    })

}

// 标记编辑器是否已经初始化过（避免 watch 触发时清空已有内容）
let isEditorInitialized = false

watch(
()=>props.templateType,
()=>{

    nextTick(()=>{

        // templateType 变化时重建，skipEmit=true 避免用占位文本覆盖父组件 keyword
        createEditor(true)
        isEditorInitialized = true

    })

},
{
    immediate:true
}
)

onMounted(()=>{

    if (!isEditorInitialized) {
      // 首次挂载需要 emit 让父组件拿到初始模板文本
      createEditor(false)
      isEditorInitialized = true
    }

})

const handleSubmit=()=>{

    emitText()

    emit(
        "submit",
        props.modelValue
    )

}
</script>

<style scoped lang="less">
.form-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #DFE6F2;
  box-sizing: border-box;
}

.content-wrap {
  flex: 1;
  overflow-y: auto;
}

.content-wrap::-webkit-scrollbar {
  width: 6px;
}

.content-wrap::-webkit-scrollbar-thumb {
  background: #D9E3F3;
  border-radius: 6px;
}

.content-wrap::-webkit-scrollbar-track {
  background: transparent;
}

/******************************
            编辑器
*******************************/

.editor {
  width: 100%;
  min-height: 134px;
  outline: none;
  padding: 4px 0;
  font-size: 20px;
  line-height: 34px;
  color: #12213F;
  white-space: normal;
  word-break: break-word;
  caret-color: #1768FF;
  user-select: text;
}

/******************************
            每一行
*******************************/

:deep(.editor-line) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

:deep(.editor-line:last-child) {
  margin-top: 18px;
  margin-bottom: 0;
}

/******************************
            普通文字
*******************************/

:deep(.editor-label) {
  color: #12213F;
  font-size: 20px;
  font-weight: 500;
  line-height: 32px;
  outline: none;
}

/******************************
            占位标签
*******************************/

:deep(.editor-token) {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 100px;
  height: 32px;

  padding: 0 14px;

  background: #EEF4FF;

  border: 1px solid #DCE8FF;

  border-radius: 8px;

  color: #6E86A8;

  font-size: 17px;

  line-height: 30px;

  cursor: text;

  transition: all .2s;

  outline: none;
}

:deep(.editor-token:hover) {
  background: #E5EEFF;
}

:deep(.editor-token:focus) {
  background: #FFFFFF;
  border-color: #1768FF;
  color: #12213F;
}

:deep(.editor-token:empty::before) {
  content: attr(data-placeholder);
  color: #90A4C5;
}

:deep(.editor-label:focus),
:deep(.editor-token:focus) {
  box-shadow: none;
}

::selection {
  background: rgba(23,104,255,.15);
}

/******************************
            底部工具栏
*******************************/

.toolbars {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.toolbars-left,
.toolbars-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/******************************
              图标
*******************************/

.icons,
.search-icon,
.tag-icon {
  width: 18px;
  height: 18px;
  margin-right: 6px;
  vertical-align: middle;
}

/******************************
            上传按钮
*******************************/

.btn-upload {
  height: 42px;
  padding: 0 18px;

  background: #fff;

  border: 1px solid #D8E0ED;

  border-radius: 10px;

  color: #31425E;

  font-size: 14px;

  font-weight: 500;

  box-shadow: 0 6px 16px rgba(36,70,130,.08);

  transition: .2s;
}

.btn-upload:hover {
  // color: #1768FF;
  border-color: #D8E0ED;
}

/******************************
            AI按钮
*******************************/

.btn-ai {
  height: 42px;

  padding: 0 18px;

  background: #F2F7FF;

  border: 1px solid #D9E7FF;

  border-radius: 10px;

  color: #1768FF;

  font-size: 14px;

  font-weight: 600;

  box-shadow: 0 6px 16px rgba(36,70,130,.08);

  transition: .2s;
}

.btn-ai:hover {
  background: #EAF3FF;
}

/******************************
            文书类型
*******************************/

.tag-btn {
  height: 42px;

  padding: 0 18px;
  line-height: 42px;

  background: #F6F8FB;

  border: 1px solid #E3EAF5;

  border-radius: 10px;

  color: #31425E;

  font-size: 14px;

  font-weight: 500;
}

/******************************
            检索按钮
*******************************/

.btn-search {
  height: 44px;

  padding: 0 24px;

  border: none;

  border-radius: 10px;

  color: #fff;

  font-size: 15px;

  font-weight: 600;

  background: linear-gradient(
    162deg,
    #1568FF 0%,
    #0F5CE6 100%
  );

// :where(.css-dev-only-do-not-override-z4nrqd).ant-btn-default:not(:disabled):hover {
//   color: #ffffff !important;
//   // border-color: #4096ff;
// }
  box-shadow:
      0 8px 18px rgba(23,104,255,.18),
      0 8px 24px rgba(36,70,130,.08);

  transition: .2s;
}

.btn-search:hover {
  opacity: .95;
  color: #ffffff !important;
}
</style>